import { useState, useEffect } from 'react'
import { apiService } from '../services/api'
import { SalesFunnel } from '../types'

export const useSalesFunnels = () => {
 const [funnels, setFunnels] = useState<SalesFunnel[]>([])
 const [defaultFunnel, setDefaultFunnel] = useState<SalesFunnel | null>(null)
 const [isLoading, setIsLoading] = useState(true)
 const [error, setError] = useState<string | null>(null)

 const fetchFunnels = async () => {
 try {
 setIsLoading(true)
 setError(null)
 const data = await apiService.getSalesFunnels()
 
 if (Array.isArray(data)) {
 setFunnels(data)
 } else {
 setFunnels([])
 }
 } catch (err) {
 console.error('Erro ao carregar funnels:', err)
 setError('Erro ao carregar configurações do funil')
 setFunnels([])
 } finally {
 setIsLoading(false)
 }
 }

 const fetchDefaultFunnel = async () => {
 try {
 setIsLoading(true)
 setError(null)
 
 // Primeiro, busca todos os funnels do usuário
 const funnelsData = await apiService.getSalesFunnels()
 
 if (Array.isArray(funnelsData) && funnelsData.length > 0) {
 // Encontra o funil padrão ou pega o primeiro
 const defaultFunnelData = funnelsData.find(f => f.isDefault) || funnelsData[0]
 setDefaultFunnel(defaultFunnelData)
 setFunnels(funnelsData)
 console.log('✅ Funil encontrado:', defaultFunnelData.name)
 return defaultFunnelData
 }
 
 // Se não tem funnels, retorna null (será criado ao salvar)
 console.log('📝 Nenhum funil configurado - será criado ao salvar')
 setDefaultFunnel(null)
 setFunnels([])
 return null
 } catch (err: any) {
 console.error('Erro ao carregar funnels:', err)
 
 // Se for 404 ou qualquer erro, assume que não tem configuração
 if (err.status === 404 || err.status === 400) {
 console.log('⚠️ Funnels não encontrados (404/400) - será criado ao salvar')
 setDefaultFunnel(null)
 setFunnels([])
 setError(null) // Não mostra erro, é esperado na primeira vez
 return null
 }
 
 setError('Erro ao carregar configurações do funil')
 return null
 } finally {
 setIsLoading(false)
 }
 }

 const createFunnel = async (funnelData: Partial<SalesFunnel>) => {
 try {
 const newFunnel = await apiService.createSalesFunnel(funnelData)
 if (newFunnel) {
 setFunnels(prev => [newFunnel, ...prev])
 return newFunnel
 }
 throw new Error('Funnel criado mas sem dados retornados')
 } catch (err) {
 console.error('Erro ao criar funnel:', err)
 setError('Erro ao criar funil. Tente novamente.')
 throw err
 }
 }

 const updateFunnel = async (funnelId: string, funnelData: Partial<SalesFunnel>) => {
 try {
 const updatedFunnel = await apiService.updateSalesFunnel(funnelId, funnelData)
 if (updatedFunnel) {
 setFunnels(prev => prev.map(f => f.id === funnelId ? updatedFunnel : f))
 if (defaultFunnel?.id === funnelId) {
 setDefaultFunnel(updatedFunnel)
 }
 return updatedFunnel
 }
 throw new Error('Funnel atualizado mas sem dados retornados')
 } catch (err) {
 console.error('Erro ao atualizar funnel:', err)
 setError('Erro ao atualizar funil. Tente novamente.')
 throw err
 }
 }

 const deleteFunnel = async (funnelId: string) => {
 try {
 await apiService.deleteSalesFunnel(funnelId)
 setFunnels(prev => prev.filter(f => f.id !== funnelId))
 if (defaultFunnel?.id === funnelId) {
 setDefaultFunnel(null)
 }
 } catch (err) {
 console.error('Erro ao deletar funnel:', err)
 setError('Erro ao deletar funil. Tente novamente.')
 throw err
 }
 }

 const updateStagesOrder = async (funnelId: string, stageIds: string[]) => {
 try {
 const updatedFunnel = await apiService.updateFunnelStagesOrder(funnelId, { stageIds })
 if (updatedFunnel) {
 setFunnels(prev => prev.map(f => f.id === funnelId ? updatedFunnel : f))
 if (defaultFunnel?.id === funnelId) {
 setDefaultFunnel(updatedFunnel)
 }
 return updatedFunnel
 }
 } catch (err) {
 console.error('Erro ao reordenar stages:', err)
 setError('Erro ao reordenar etapas. Tente novamente.')
 throw err
 }
 }

 useEffect(() => {
 fetchDefaultFunnel()
 }, [])

 return {
 funnels,
 defaultFunnel,
 isLoading,
 error,
 fetchFunnels,
 fetchDefaultFunnel,
 createFunnel,
 updateFunnel,
 deleteFunnel,
 updateStagesOrder
 }
}

